import { ClientQuoteDetailsApi } from './clientQuoteDetailsApi';

function formatEstimateData(estimate) {
  let parsed = {};

  // 1. Parse seguro do JSON "itens"
  if (typeof estimate.itens === 'string') {
    try {
      parsed = JSON.parse(estimate.itens || '{}');
    } catch (e) {
      console.error("Erro no parse de itens:", e);
      parsed = {};
    }
  } else if (typeof estimate.itens === 'object' && estimate.itens !== null) {
    parsed = estimate.itens;
  }

  // 2. Se for um objeto com estrutura nova (contém equipamento_info, equipamentos, servicos, localizacao),
  //    preservar a estrutura completa para a view poder exibir os dados de equipamento_info.
  let itensResult;

  if (!Array.isArray(parsed) && typeof parsed === 'object') {
    // Parse equipamento_info se for string
    let eqInfo = parsed.equipamento_info || null;
    if (typeof eqInfo === 'string') {
      try {
        eqInfo = JSON.parse(eqInfo);
      } catch (e) {
        // Ignora erro de parse
      }
    }

    // Mapear servicos se existirem
    let servicosRaw = Array.isArray(parsed.servicos) ? parsed.servicos : [];
    const servicosFormatados = servicosRaw.map(s => {
      let sEqInfo = s.equipamento_info;
      if (typeof sEqInfo === 'string') {
        try {
          sEqInfo = JSON.parse(sEqInfo);
        } catch (e) {
          // Ignora erro de parse
        }
      }

      return {
        servico: s.servico_nome || s.nome || "Serviço",
        obs: parsed.problem || s.problem || s.obs || "",
        quantidade: s.quantidade || s.quantity || 1,
        equipamento_info: sEqInfo,
        valor: s.valor || 0
      };
    });

    // Preservar a estrutura completa do objeto
    itensResult = {
      servicos: servicosFormatados,
      equipamentos: Array.isArray(parsed.equipamentos) ? parsed.equipamentos : [],
      equipamento_info: eqInfo,
      localizacao: parsed.localizacao || null,
      apontamentos: parsed.apontamentos || null,
      user_id: parsed.user_id || null
    };
  } else if (Array.isArray(parsed)) {
    // Fallback: se for array direto na raiz do JSON itens
    itensResult = parsed.map(s => {
      let eqInfo = s.equipamento_info;
      if (typeof eqInfo === 'string') {
        try {
          eqInfo = JSON.parse(eqInfo);
        } catch (e) {
          // Ignora erro de parse
        }
      }

      return {
        servico: s.servico_nome || s.nome || "Serviço",
        obs: s.problem || s.obs || "",
        quantidade: s.quantidade || s.quantity || 1,
        equipamento_info: eqInfo,
        valor: s.valor || 0
      };
    });
  } else {
    itensResult = [];
  }

  // 3. Endereço
  const localizacao = parsed.localizacao || {};
  const enderecoFinal = localizacao.endereco || parsed.maintenanceLocalization || parsed.endereco || estimate.endereco || estimate.address;

  return {
    id: estimate.id,
    status: estimate.status,
    endereco: enderecoFinal,
    itens: itensResult,
    texto_resposta: estimate.textoResposta || estimate.responseText,
    valor_resposta: estimate.valorResposta || estimate.responseValue,
    motivo_recusa: estimate.motivoRecusa || estimate.refusalReason,
    created_at: estimate.created_at || estimate.created_at,
    updated_at: estimate.updated_at || estimate.updated_at
  };
}

function formatBudgetData(budget) {
  return {
    id: budget.id,
    status: budget.status,
    valor: budget.valor || budget.value,
    observacoes: budget.observacoes || budget.observations,
    itens: Array.isArray(budget.itens) ? budget.itens.map(item => ({
      equip_id: item.equip_id || item.equipment_id,
      equipamento_nome: item.equipamento_nome || item.equipment_name,
      equipamento_marca: item.equipamento_marca || item.equipment_brand,
      equipamento_modelo: item.equipamento_modelo || item.equipment_model,
      equipamento_serie: item.equipamento_serie || item.equipment_serial,
      tipo_item: item.tipo_item || item.item_type,
      codigo_interno: item.codigo_interno || item.internal_code,
      nome: item.nome || item.name,
      qtd: item.qtd || item.quantity,
      valor: item.valor || item.value
    })) : [],
    created_at: budget.created_at || budget.created_at,
    updated_at: budget.updated_at || budget.updated_at
  };
}

function formatClientData(client) {
  return {
    empresa: client.empresa || client.company,
    nome: client.nome || client.name,
    telefone: client.telefone || client.phone,
    cnpj: client.cnpj || client.cnpj,
    dt_criacao: client.dt_criacao || client.created_at,
    email: client.email || client.email
  };
}

function formatQuoteDetailsData(data) {
  return {
    cliente: data.cliente ? formatClientData(data.cliente) : null,
    estimativa: data.estimativa ? formatEstimateData(data.estimativa) : null,
    orcamento: data.orcamento ? formatBudgetData(data.orcamento) : null,
    serviceFiles: Array.isArray(data.serviceFiles) ? data.serviceFiles : []
  };
}

function formatarMoeda(valor) {
  if (valor == null || valor === "") return "-";
  return Number(valor).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
}

function groupBudgetItemsByEquipment(itens) {
  const agrupados = {};

  itens.forEach(item => {
    if (!agrupados[item.equip_id]) {
      agrupados[item.equip_id] = [];
    }
    agrupados[item.equip_id].push(item);
  });

  return agrupados;
}

function calculateBudgetTotals(itens) {
  let totalPecas = 0;
  let totalProcedimentos = 0;

  itens.forEach(item => {
    const qtd = Number(item.qtd) || 1;
    const valor = Number(item.valor) || 0;
    const total = qtd * valor;

    if (item.tipo_item === "peca") {
      totalPecas += total;
    } else if (item.tipo_item === "procedimento") {
      totalProcedimentos += total;
    }
  });

  return {
    totalPecas,
    totalProcedimentos,
    totalGeral: totalPecas + totalProcedimentos
  };
}

// Funções principais do serviço
function getQuoteDetails(id) {
  return ClientQuoteDetailsApi.getQuoteDetails(id)
    .then(response => formatQuoteDetailsData(response.data));
}

function acceptEstimate(id, notaRemessaFile) {
  const formData = new FormData();
  if (notaRemessaFile) {
    formData.append('nota_remessa', notaRemessaFile);
  }

  return ClientQuoteDetailsApi.acceptEstimate(id, formData)
    .then(response => ({
      success: true,
      data: response.data,
      message: "Estimativa aceita com sucesso"
    }));
}

function refuseEstimate(id, userId, motivo) {
  const data = { userId, motivo };
  return ClientQuoteDetailsApi.refuseEstimate(id, data)
    .then(response => ({
      success: true,
      data: response.data,
      message: "Estimativa recusada com sucesso"
    }));
}

function refuseQuotation(id, userId, motivo) {
  const data = { userId, motivo };
  return ClientQuoteDetailsApi.refuseQuotation(id, data)
    .then(response => ({
      success: true,
      data: response.data,
      message: "Cotação recusada com sucesso"
    }));
}

function acceptBudget(id) {
  return ClientQuoteDetailsApi.acceptBudget(id)
    .then(response => ({
      success: true,
      data: response.data,
      message: "Orçamento aprovado com sucesso"
    }));
}

function refuseBudget(id, motivo) {
  return ClientQuoteDetailsApi.refuseBudget(id, motivo)
    .then(response => ({
      success: true,
      data: response.data,
      message: "Orçamento recusado com sucesso"
    }));
}

function getShippingDetails(id) {
  return ClientQuoteDetailsApi.getShippingDetails(id)
    .then(response => response.data);
}

export const ClientQuoteDetailsService = {
  getQuoteDetails,
  acceptEstimate,
  refuseEstimate,
  refuseQuotation,
  acceptBudget,
  refuseBudget,
  getShippingDetails,
  formatEstimateData,
  formatBudgetData,
  formatClientData,
  formatQuoteDetailsData,
  formatarMoeda,
  groupBudgetItemsByEquipment,
  calculateBudgetTotals
};
